import { useState } from "react";

interface UseSelectionProps<T> {
  data: T[];
  getId: (item: T) => string;
}

export interface CheckboxSelectionResult {
  selectedItems: string[];
  selectAll: boolean;
  toggleSelectAll: () => void;
  toggleSelectItem: (itemId: string, e?: React.MouseEvent) => void;
  clearSelection: () => void;
  isSelected: (itemId: string) => boolean;
  selectedCount: number;
}

export const useCheckboxSelection = <T>({
  data,
  getId,
}: UseSelectionProps<T>): CheckboxSelectionResult => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map((item) => getId(item)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectItem = (itemId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
      setSelectAll(false);
    } else {
      setSelectedItems([...selectedItems, itemId]);
      if (selectedItems?.length + 1 === data?.length) {
        setSelectAll(true);
      }
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectAll(false);
  };

  const isSelected = (itemId: string) => selectedItems.includes(itemId);

  return {
    selectedItems,
    selectAll,
    toggleSelectAll,
    toggleSelectItem,
    clearSelection,
    isSelected,
    selectedCount: selectedItems?.length,
  };
};
